import { omit } from 'lodash';
import { exploration, model } from '@gosupersimple/types';

import { nameToKey } from '@/explore/edit-pipeline/utils';
import { formatPropertyValue } from '@/explore/utils/format';

import { flattenNestedList, groupRecordsByProperties } from '../grouping';
import {
  DataTableProperty,
  DataTableRow,
  entityPropertyValueToLiteral,
  Field,
  PipelineState,
} from '../types';

const pivotLeafChildren = (
  children: DataTableRow[],
  column: exploration.PivotOptionsColumn,
  valuesKey: string,
) =>
  Object.fromEntries(
    (column.snapshot?.items ?? [])
      .map((col) => [col.key, children.find((r) => r[column.key] === col.value)?.[valuesKey]])
      .filter(([, v]) => v !== undefined),
  );

const applyPivotToLeaves = (
  nodes: DataTableRow[],
  column: exploration.PivotOptionsColumn,
  valuesKey: string,
): DataTableRow[] =>
  nodes.map((node) => {
    if (!('$children' in node) || node.$children === undefined) {
      return node;
    }

    const children = node.$children;
    const firstChild = children.at(0);

    if (firstChild !== undefined && '$children' in firstChild) {
      const $children = applyPivotToLeaves(children, column, valuesKey);
      // Sum each child's leaf count (pivoted-leaf rows have no $leaves and count as 1) so that
      // the leaf total propagates correctly through 3+ grouping levels.
      const $leaves = $children.reduce((sum, c) => sum + (c.$leaves ?? 1), 0);
      return { ...node, $leaves, $children };
    }

    return {
      ...omit(node, ['$children', '$leaves']),
      ...pivotLeafChildren(children, column, valuesKey),
    };
  });

export const isPivotApplicableOnState = (
  state: PipelineState,
  pivotOptions: exploration.PivotOptions,
) => {
  const valuesKey = pivotOptions.values.at(0)?.key;

  if (valuesKey === undefined) {
    return false;
  }

  const valuesField = state.fields.find((f) => f.key === valuesKey);

  if (valuesField === undefined) {
    return false;
  }

  // Pivoting collapses multiple tall records into a single cell via `children.find(...)` in
  // `pivotLeafChildren` — that only makes sense when the value is an aggregation. Otherwise
  // we'd silently drop duplicates. Guard against stale pivot configs whose values key points
  // at a field that's no longer aggregated.
  if (valuesField.aggregationType === undefined) {
    return false;
  }

  // A pivot column entry without a snapshot is "needs refetch" — render un-pivoted until the
  // refetch effect in records-cell-view fills it in. Otherwise the pivot would render with no
  // columns at all.
  if (pivotOptions.columns.some((col) => col.snapshot === undefined)) {
    return false;
  }

  return (
    pivotOptions.rows.every((row) => state.fields.some((f) => f.key === row.key)) &&
    pivotOptions.columns.every((col) => state.fields.some((f) => f.key === col.key))
  );
};

export const applyPivotOnRecords = (
  records: DataTableRow[],
  pivotOptions: exploration.PivotOptions,
) => {
  if (!pivotOptions.enabled) {
    return records;
  }

  const [column] = pivotOptions.columns;
  const [values] = pivotOptions.values;

  const flatRecords = flattenNestedList(records);
  if (pivotOptions.rows.length === 0) {
    return [pivotLeafChildren(flatRecords, column, values.key)];
  }

  const grouped = groupRecordsByProperties(flatRecords, pivotOptions.rows);

  return applyPivotToLeaves(grouped, column, values.key);
};

export const applyPivotOnState = (
  state: PipelineState,
  pivotOptions: exploration.PivotOptions,
): PipelineState<DataTableProperty[]> => {
  if (!pivotOptions.enabled) {
    return state;
  }

  const [column] = pivotOptions.columns;
  const [values] = pivotOptions.values;
  const groupKeys = pivotOptions.rows.map((r) => r.key);
  const fields = groupKeys
    .map((key) => state.fields.find((f) => f.key === key))
    .filter((f): f is Field => f !== undefined);

  const aggregatedColumn = state.fields.find((f) => f.key === values.key);
  const columns = column.snapshot?.items ?? [];

  const pivotFields = [
    ...fields,
    ...columns.map(
      ({ key, value, name }) =>
        ({
          key,
          name: name ?? key,
          type: aggregatedColumn?.type ?? 'Number',
          precision: aggregatedColumn?.precision,
          format: aggregatedColumn?.format,
          lineage: {
            kind: 'pivot',
            valueKey: values.key,
            columnValue: entityPropertyValueToLiteral(value),
            columnKey: column.key,
          },
          aggregationType: aggregatedColumn?.aggregationType,
        }) satisfies DataTableProperty,
    ),
  ];

  return { ...state, fields: pivotFields, grouping: groupKeys.map((key) => ({ key })) };
};

/**
 * Decide what to do with a persisted pivot when the pipeline state changes.
 *
 * Implemented:
 *   - row reconciliation + clearing of missing column/values keys (delegated to applyStateOnPivot)
 *   - snapshot's `precision` no longer matches the current column field's precision
 *       → drop the snapshot so records-cell-view's refetch effect rebuilds it
 *
 * Still to do:
 *   - column key gone from non-aggregated fields → set `enabled: false`, `disabledReason: 'columnFieldChanged'`
 *   - values key gone or no longer aggregated → set `enabled: false`, `disabledReason: 'valuesFieldChanged'`
 *   - retire applyStateOnPivot once `disabledReason` is the only "broken" signal
 *
 * Called from three seams:
 *   - `adjustPipelineOptions` (mutation-time, every pipeline edit)
 *   - `ensureValidPivot`      (load-time, every cell read from server)
 *   - render-side guards via `isPivotApplicableOnState`, which short-circuits when
 *     the snapshot is absent — so a stale repair triggers the refetch effect automatically
 */
export const repairPivot = (
  state: PipelineState,
  pivotOptions: exploration.PivotOptions,
): exploration.PivotOptions => {
  // Row reconciliation + missing-key clearing first; further repair runs on top of it.
  const reconciled = applyStateOnPivot(state, pivotOptions);

  if (!reconciled.enabled) {
    return reconciled;
  }

  const columnEntry = reconciled.columns.at(0);
  if (columnEntry === undefined || columnEntry.key === '' || columnEntry.snapshot === undefined) {
    // Nothing to invalidate: applyStateOnPivot either blanked the key (column field gone) or
    // there's no cached snapshot yet (first toggle, or a previous repair already dropped it).
    return reconciled;
  }

  const columnField = state.fields.find((f) => f.key === columnEntry.key);
  if (columnField === undefined) {
    // Defensive: applyStateOnPivot should have blanked the key above.
    return reconciled;
  }

  // Snapshot was captured under one precision; the column field now reports a different one.
  // The cached items reflect a different aggregation of the same source data (e.g. month →
  // day collapses many distinct values into one and produces wrong pivot columns). Drop the
  // snapshot so the records cell's refetch effect rebuilds it under the new precision.
  if (columnEntry.snapshot.precision !== columnField.precision) {
    return {
      ...reconciled,
      columns: [{ key: columnEntry.key }],
    };
  }

  return reconciled;
};

export const applyStateOnPivot = (
  state: PipelineState,
  pivotOptions: exploration.PivotOptions,
): exploration.PivotOptions => {
  const groupable = state.fields.filter((f) => f.aggregationType === undefined);
  const aggregated = state.fields.filter((f) => f.aggregationType !== undefined);

  const [column] = pivotOptions.columns;
  const [values] = pivotOptions.values;

  const columnKey = groupable.some((f) => f.key === column.key) ? column.key : '';
  const valuesKey = aggregated.some((f) => f.key === values.key) ? values.key : '';

  // Preserve the user's existing row order for any keys that still exist in the pipeline state,
  // then append any newly-introduced groupable fields at the end.
  const candidateRows = groupable.filter((f) => f.key !== columnKey);
  const existingRows = pivotOptions.rows
    .filter((r) => candidateRows.some((f) => f.key === r.key))
    .map((r) => ({ key: r.key }));
  const existingKeys = new Set(existingRows.map((r) => r.key));
  const newRows = candidateRows
    .filter((f) => !existingKeys.has(f.key))
    .map((f) => ({ key: f.key }));
  const rows = [...existingRows, ...newRows];

  return {
    ...pivotOptions,
    columns: [columnKey !== '' ? column : { key: '', snapshot: { items: [] } }],
    rows,
    values: [{ key: valuesKey }],
  };
};

export const generateColumnKey = (
  value: unknown,
  field: { type: model.PropertyType | null },
  idx: number,
  maxKeyLength = 20,
) => {
  const prefix = `_pivot_${idx}`;
  const maxLength = maxKeyLength - (prefix.length + 1);

  if (value === null || value === undefined) {
    return `${prefix}_null`;
  }

  if (field.type === 'Boolean') {
    return `${prefix}_${value === true ? 'true' : 'false'}`;
  }

  if (field.type === 'Date' && typeof value === 'string') {
    const dateKey = nameToKey(value).replace(/^_+|_+$/g, '');
    return dateKey.length > 0 ? `${prefix}_${dateKey.slice(0, maxLength)}` : prefix;
  }

  const key = nameToKey(String(value)).replace(/^_+|_+$/g, '');
  if (key.length === 0) {
    return prefix;
  }

  return `${prefix}_${key.slice(0, maxLength)}`;
};

export const formatColumnValue = (value: unknown, field: { type: model.PropertyType | null }) => {
  const { type } = field;

  if (value === null || value === undefined) {
    return null;
  }

  if (type === 'Boolean' && typeof value === 'boolean') {
    return value;
  }

  if (type === 'Date' && value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
};

export const formatColumnName = (value: unknown, field: Field) => {
  const { type } = field;

  if (type === 'Date' && typeof value === 'string') {
    return formatPropertyValue(value, { field });
  }
  if (type === 'Date' && value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (value === null || value === undefined) {
    return '-';
  }
  return String(value);
};
